/**
 * Diagram content schema — the System Context shape (issue #1077, epic #1002)
 *
 * `Diagram.content` is a GraphQL `JSON!` scalar: untyped at the wire boundary
 * by design (the field is format-generic across c4/mermaid, issue #1072).
 * This is the only shape `DiagramGenerationService.generateSystemContext`
 * (backend/src/services/DiagramGenerationService.ts, issue #1075) produces
 * today, and it mirrors that service's `C4SystemContextSource` interface
 * field-for-field. Every other `DiagramType` (container, component, mermaid,
 * ...) has no generation algorithm yet, so a diagram of one of those types
 * would fail this parse — `DiagramViewer` treats that as "cannot render this
 * diagram" rather than guessing at an unknown shape.
 */

import { z } from 'zod';

export const c4SystemContextPersonSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('team'),
  description: z.string().optional(),
});

export const c4SystemContextExternalSystemSchema = z.object({
  id: z.string(),
  name: z.string(),
  entityType: z.union([z.literal('Service'), z.literal('Technology')]),
  description: z.string().optional(),
});

export const c4SystemContextRelationshipSchema = z.object({
  fromId: z.string(),
  toId: z.string(),
  direction: z.union([z.literal('outbound'), z.literal('inbound')]),
  description: z.string(),
  technology: z.string().optional(),
  criticality: z.string().optional(),
});

export const c4SystemContextContentSchema = z.object({
  system: z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
  }),
  people: z.array(c4SystemContextPersonSchema),
  externalSystems: z.array(c4SystemContextExternalSystemSchema),
  relationships: z.array(c4SystemContextRelationshipSchema),
});

export type C4SystemContextPerson = z.infer<typeof c4SystemContextPersonSchema>;
export type C4SystemContextExternalSystem = z.infer<typeof c4SystemContextExternalSystemSchema>;
export type C4SystemContextRelationship = z.infer<typeof c4SystemContextRelationshipSchema>;
export type C4SystemContextContent = z.infer<typeof c4SystemContextContentSchema>;
